import { useMemo } from 'react';
import { useSessionClaims } from '../lib/auth';

/**
 * Query-key factory. Every key starts with the tenant id from the session claims, so two tenants
 * never share cached rows and `queryClient.removeQueries({ queryKey: keys.all })` wipes a
 * tenant on sign-out. Parameters are appended as plain objects (TanStack hashes them stably).
 */

export type ListParams = Record<string, string | number | boolean | null | undefined>;

const clean = (params?: ListParams): ListParams => {
  if (!params) return {};
  const out: ListParams = {};
  for (const [k, v] of Object.entries(params)) if (v !== undefined) out[k] = v;
  return out;
};

export function queryKeys(tenantId: string) {
  const root = <S extends string>(scope: S) => [tenantId, scope] as const;
  const resource = <S extends string>(scope: S) => {
    const all = root(scope);
    return {
      all,
      list: (params?: ListParams) => [...all, 'list', clean(params)] as const,
      detail: (id: string) => [...all, 'detail', id] as const,
    };
  };

  const projectScope = (projectId: string) => {
    const scope = [tenantId, 'project', projectId] as const;
    return {
      scope,
      issueTypes: [...scope, 'issue-types'] as const,
      priorities: [...scope, 'priorities'] as const,
      fields: (params?: ListParams) => [...scope, 'fields', clean(params)] as const,
      bindings: [...scope, 'field-bindings'] as const,
      forms: (params?: ListParams) => [...scope, 'forms', 'list', clean(params)] as const,
      formsAll: [...scope, 'forms'] as const,
      form: (formId: string) => [...scope, 'forms', 'detail', formId] as const,
      resolvedForm: (formId: string) => [...scope, 'forms', 'resolved', formId] as const,
      workflows: [...scope, 'workflows'] as const,
      workflow: (workflowId: string) => [...scope, 'workflows', 'detail', workflowId] as const,
      workflowVersions: (workflowId: string) =>
        [...scope, 'workflows', 'versions', workflowId] as const,
      workflowVersion: (workflowId: string, version: number) =>
        [...scope, 'workflows', 'versions', workflowId, version] as const,
      roles: [...scope, 'roles'] as const,
      role: (roleId: string) => [...scope, 'roles', 'detail', roleId] as const,
      roleAssignments: (roleId: string) => [...scope, 'roles', 'assignments', roleId] as const,
      assignments: [...scope, 'assignments'] as const,
      effectivePermissions: (userId?: string) =>
        [...scope, 'effective-permissions', userId ?? 'me'] as const,
      slaPolicies: [...scope, 'sla-policies'] as const,
      slaPolicy: (policyId: string) => [...scope, 'sla-policies', 'detail', policyId] as const,
      cannedResponses: (params?: ListParams) =>
        [...scope, 'canned-responses', clean(params)] as const,
    };
  };

  const ticketScope = (idOrKey: string) => {
    const scope = [tenantId, 'tickets', 'detail', idOrKey] as const;
    return {
      scope,
      detail: scope,
      transitions: [...scope, 'transitions'] as const,
      comments: [...scope, 'comments'] as const,
      watchers: [...scope, 'watchers'] as const,
      links: [...scope, 'links'] as const,
      history: [...scope, 'history'] as const,
      attachments: [...scope, 'attachments'] as const,
      sla: [...scope, 'sla'] as const,
    };
  };

  const fields = resource('fields');
  const search = root('search');
  const portal = root('portal');

  return {
    /** Everything cached for this tenant. */
    all: [tenantId] as const,
    tenant: root('tenant'),
    me: root('me'),
    users: {
      ...resource('users'),
      membership: (userId: string) => [tenantId, 'users', 'membership', userId] as const,
    },
    groups: {
      ...resource('groups'),
      members: (groupId: string) => [tenantId, 'groups', 'members', groupId] as const,
    },
    organizations: {
      ...resource('organizations'),
      members: (organizationId: string) =>
        [tenantId, 'organizations', 'members', organizationId] as const,
    },
    audit: resource('audit'),
    projects: resource('projects'),
    project: projectScope,
    fields: {
      ...fields,
      options: (fieldId: string) => [...fields.all, 'options', fieldId] as const,
    },
    templates: resource('project-templates'),
    cannedResponses: resource('canned-responses'),
    roles: resource('roles'),
    permissionSchemes: resource('permission-schemes'),
    views: {
      ...resource('views'),
      run: (viewId: string) => [tenantId, 'views', 'run', viewId] as const,
    },
    jobs: resource('jobs'),
    apiKeys: resource('api-keys'),
    webhooks: {
      ...resource('webhooks'),
      deliveries: (subscriptionId: string, params?: ListParams) =>
        [tenantId, 'webhooks', 'deliveries', subscriptionId, clean(params)] as const,
    },
    linkTypes: resource('link-types'),
    /** Bolt 10: addresses the ses-events worker suppressed after a bounce or complaint. */
    emailSuppressions: resource('email-suppressions'),
    /** Bolt 9: import batches (JSM/Monday.com/CSV), their mapping and dry-run report. */
    imports: {
      ...resource('imports'),
      mapping: (importId: string) => [tenantId, 'imports', 'mapping', importId] as const,
      report: (importId: string) => [tenantId, 'imports', 'report', importId] as const,
    },
    /** Bolt 8: automation rules + their run history (Unit 07). */
    automations: {
      ...resource('automations'),
      runs: (ruleId: string, params?: ListParams) =>
        [tenantId, 'automations', 'runs', ruleId, clean(params)] as const,
    },
    /** Bolt 8: approvals inbox (Unit 07); rows are per-approver, so params include `mine`/`decision`. */
    approvals: {
      all: root('approvals'),
      list: (params?: ListParams) => [tenantId, 'approvals', 'list', clean(params)] as const,
    },
    /** Bolt 8: in-app notification inbox + the caller's channel preferences (Unit 08). */
    notifications: {
      all: root('notifications'),
      list: (params?: ListParams) => [tenantId, 'notifications', 'list', clean(params)] as const,
      preferences: [tenantId, 'notifications', 'preferences'] as const,
    },
    tickets: {
      all: root('tickets'),
      ...ticketScope,
    },
    ticket: ticketScope,
    search: {
      all: search,
      results: (request: unknown) => [...search, 'search', request] as const,
      count: (filter: unknown) => [...search, 'count', filter ?? null] as const,
    },
    /** Portal metadata (`/v1/projects`, request types, resolved portal forms, branding, me). */
    portal: {
      all: portal,
      projects: (params?: ListParams) => [...portal, 'projects', clean(params)] as const,
      requestTypes: (projectId: string) => [...portal, 'request-types', projectId] as const,
      statuses: (projectId: string) => [...portal, 'statuses', projectId] as const,
      resolvedForm: (projectId: string, formId: string) =>
        [...portal, 'forms', 'resolved', projectId, formId] as const,
      branding: [...portal, 'branding'] as const,
      me: [...portal, 'me'] as const,
    },
  };
}

export type QueryKeys = ReturnType<typeof queryKeys>;

/** Keys namespaced by the current tenant (from the session claims). */
export function useQueryKeys(): QueryKeys {
  const { tenantId } = useSessionClaims();
  return useMemo(() => queryKeys(tenantId), [tenantId]);
}
